import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en/app.json' with { type: 'json' };
import { PUBLIC_NAME } from './lib/config.js';
import { logger } from './lib/logger.js';

const strict = import.meta.env.MODE !== 'production';
const reported = new Set<string>();

/** A missing text key never reaches a screen: it fails loudly in test and development, and shows nothing in production. */
function onMissingKey(_lngs: readonly string[], ns: string, key: string): void {
  if (strict) throw new Error(`Missing text key: ${ns}:${key}`);
  if (reported.has(key)) return;
  reported.add(key);
  logger.warn('missing text key', { ns, key });
}

export const LANGUAGES = ['en', 'hi', 'mr'] as const;
export type Language = (typeof LANGUAGES)[number];
const LANG_KEY = 'sauda.lang';

export function readLanguage(): Language {
  try {
    const language = window.localStorage.getItem(LANG_KEY);
    return language === 'hi' || language === 'mr' ? language : 'en';
  } catch {
    return 'en';
  }
}

/** Switch the app language. Hindi and Marathi load on demand so English users never download them. */
export async function setLanguage(language: Language): Promise<void> {
  if (language !== 'en' && !i18n.hasResourceBundle(language, 'translation')) {
    const resources =
      language === 'hi'
        ? await import('./locales/hi/app.json', { with: { type: 'json' } })
        : await import('./locales/mr/app.json', { with: { type: 'json' } });
    i18n.addResourceBundle(language, 'translation', resources.default, true, true);
  }
  await i18n.changeLanguage(language);
  document.documentElement.lang = language;
  try {
    window.localStorage.setItem(LANG_KEY, language);
  } catch {
    // The language remains selected for this visit when storage is unavailable.
  }
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
  },
  lng: 'en',
  fallbackLng: 'en',
  saveMissing: true,
  returnNull: false,
  missingKeyHandler: onMissingKey,
  parseMissingKeyHandler: () => '',
  interpolation: {
    escapeValue: false,
    defaultVariables: { name: PUBLIC_NAME },
  },
});

const savedLanguage = readLanguage();
if (savedLanguage !== 'en') void setLanguage(savedLanguage);

export default i18n;
